import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { ActivityDto, ActivityRowType } from '../../services/api.types'
import { Capacitor } from '@capacitor/core'
import { jt, t } from 'ttag'
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet'
import { accessibleGray, red } from '../../styles/material-ui-theme-v2'
import YumDialogTitle from '../YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../YumDialogContent/YumDialogContent'
import YumDialogContentText from '../YumDialogContentText/YumDialogContentText'
import YumDialogActions from '../YumDialogActions/YumDialogActions'
import YumCancelButton from '../YumCancelButton/YumCancelButton'
import YumSubmitButton from '../YumSubmitButton/YumSubmitButton'
import YumDialog from '../YumDialog/YumDialog'
import YumButton from '../YumButton/YumButton'

import { ReactComponent as DeleteIcon } from '../../assets/icons/trash.svg'
import { ReactComponent as MoreIcon } from '../../assets/v3/icons/more.svg'
import { getWorkoutLabel } from '../../common/workoutActivities'

const useStyles = makeStyles((theme) => ({
  contextMenu: {
    position: 'relative',
  },
  dropdown: {
    zIndex: 5,
    position: 'absolute',
    left: 'auto',
    right: 0,
    top: -28,
    margin: '0.7rem 0 0',
    paddingLeft: 0,
    paddingRight: 0,
    opacity: 0,
    listStyle: 'none',
    transition: 'all 0.2s ease-in-out',
    pointerEvents: 'none',
    borderRadius: 3,
    background: 'white',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    '&.open': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    '& li:not(:last-child)': {
      borderBottom: '1px solid #e0e0e0',
    },
  },
  dropdownRtl: {
    left: 0,
    right: 'auto',
  },
  link: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem',
    whiteSpace: 'nowrap',
    background: 'none',
    border: 'none',
    color: '#333',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'normal',
    opacity: 1,
    cursor: 'pointer',
    letterSpacing: '0.65px',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  icon: {
    width: '1.25rem',
    height: '1.25rem',
    marginRight: '0.5rem',
  },
  iconRtl: {
    marginRight: 0,
    marginLeft: '0.5rem',
  },
  backdrop: {
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 4,
  },
  moreButton: {
    transition: 'background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#f5f5f5 !important',
    },
    '&:focus': {
      backgroundColor: '#f0f0f0 !important',
    },
  },
}))

export interface ActivityActionsProps {
  className?: string
  activity: ActivityDto
  onDeleteActivity?: (activityId: string) => void
}

export default function ActivityActions({ className, activity, onDeleteActivity }: ActivityActionsProps) {
  // Initialize Material-UI styles
  const classes = useStyles()

  // State management for UI interactions
  const [contextMenuVisible, setContextMenuVisible] = useState(false)
  const [deleteActivityModalVisible, setDeleteActivityModalVisible] = useState(false)

  const activityName = <strong key={'title'}>{activity.activityType}</strong>

  const toggleContextMenu = (event?: any) => {
    if (event) event.preventDefault()
    setContextMenuVisible(!contextMenuVisible)
  }

  const toggleDeleteActivityModal = () => setDeleteActivityModalVisible(!deleteActivityModalVisible)

  const onDeleteActivityClicked = (event?: any) => {
    if (event) event.preventDefault()
    toggleContextMenu()
    toggleDeleteActivityModal()
  }

  const onDeleteActivityConfirmed = () => {
    toggleDeleteActivityModal()
    if (onDeleteActivity) {
      onDeleteActivity(activity.id)
    }
  }

  const onContextMenuClicked = (event: any) => {
    event.preventDefault()

    if (Capacitor.getPlatform() === 'web') {
      return toggleContextMenu()
    }

    try {
      const showActions = async () => {
        const options = [
          {
            title: t`Delete activity`,
            style: ActionSheetButtonStyle.Destructive,
          },
          {
            title: t`Cancel`,
            style: ActionSheetButtonStyle.Cancel,
          },
        ]

        const result = await ActionSheet.showActions({
          title: getWorkoutLabel(activity.normalizedType),
          options,
        })

        if (result && result.index === 0) {
          toggleDeleteActivityModal()
        }
      }

      showActions()
    } catch (e) {
      return toggleContextMenu()
    }
  }

  const preventRedirection = (event: any) => event.stopPropagation()

  return (
    <div className={className} onClick={preventRedirection}>
      <div className={classes.contextMenu}>
        <ul className={`${classes.dropdown} ${contextMenuVisible ? 'open' : ''}`} style={{ top: '30px' }}>
          <li>
            <button className={classes.link} type="button" onClick={onDeleteActivityClicked} style={{ color: red }}>
              <DeleteIcon className={classes.icon} />
              {t`Delete activity`}
            </button>
          </li>
        </ul>
        {contextMenuVisible && <div className={classes.backdrop} onClick={toggleContextMenu} />}
      </div>

      <YumDialog open={deleteActivityModalVisible}>
        <YumDialogTitle>{t`Delete activity`}</YumDialogTitle>
        <YumDialogContent>
          <YumDialogContentText>
            {jt`Are you sure you want to delete this activity? Once deleted, you can't undo it.`}
          </YumDialogContentText>
        </YumDialogContent>
        <YumDialogActions>
          <YumCancelButton onClick={toggleDeleteActivityModal} />
          <YumSubmitButton text={t`Delete`} onClick={onDeleteActivityConfirmed} color="error" />
        </YumDialogActions>
      </YumDialog>

      <YumButton
        role="button"
        type="button"
        aria-label={t`More`}
        aria-haspopup="menu"
        aria-expanded={contextMenuVisible}
        onClick={onContextMenuClicked}
        variant="outlined"
        size="small"
        className={classes.moreButton}
        style={{ color: accessibleGray, borderColor: 'transparent' }}
      >
        <MoreIcon aria-hidden="true" />
      </YumButton>
    </div>
  )
}
